/**
 * Header Component
 * Phase 6: Frontend Application
 * Updated Phase 6A: Added Dashboard link
 *
 * Main navigation header with logo, nav links, and user menu.
 */

'use client';

import { UserMenu } from '@/components/auth';
import { HealthIndicator } from '@/components/layout/health-indicator';
import { InvitationsBell } from '@/components/layout/invitations-bell';
import { useDbWakeOnLogin } from '@/hooks/use-db-wake-on-login';
import { ROLES, useAuth } from '@/lib/auth';
import { ChefHat, Menu, X } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

interface NavLink {
  href: string;
  label: string;
  requiresAuth?: boolean;
  roles?: string[];
}

const navLinks: NavLink[] = [
  { href: '/dashboard', label: 'Dashboard', requiresAuth: true },
  { href: '/recipes', label: 'Recipes', requiresAuth: true },
  { href: '/menus', label: 'Meal Plans', requiresAuth: true },
  { href: '/guides', label: 'Guides', requiresAuth: true },
  { href: '/profile', label: 'My Profile', requiresAuth: true },
  { href: '/review', label: 'Review Queue', requiresAuth: true, roles: [ROLES.ADMIN, ROLES.TRUSTED_EDITOR] },
];

export function Header(): JSX.Element {
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const { isAuthenticated, user } = useAuth();
  useDbWakeOnLogin(isAuthenticated);
  const hamburgerRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Close the dropdown when clicking/tapping outside it or pressing Escape
  useEffect(() => {
    if (!menuOpen) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent): void => {
      const target = event.target as Node | null;
      if (!target) return;
      if (hamburgerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setMenuOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  // Filter nav links based on auth state and roles
  const visibleLinks = navLinks.filter((link) => {
    if (link.requiresAuth && !isAuthenticated) return false;
    if (link.roles && (!user?.roles || !link.roles.some(role => user.roles.includes(role)))) return false;
    return true;
  });

  return (
    <header className="sticky top-0 z-40 border-b border-gray-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Left side: Hamburger menu (only when authenticated) + Logo */}
          <div className="flex items-center gap-2">
            {/* Hamburger menu button - only show when authenticated */}
            {isAuthenticated && (
              <button
                ref={hamburgerRef}
                onClick={() => setMenuOpen(!menuOpen)}
                className="rounded-lg p-2 text-gray-600 hover:bg-gray-100"
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              >
                {menuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            )}

            {/* Logo */}
            <Link href={isAuthenticated ? '/dashboard' : '/'} className="flex items-center gap-2">
              <ChefHat className="h-8 w-8 text-primary-600" />
              <span className="font-display text-xl font-bold text-gray-900">
                Recipe Manager
              </span>
            </Link>
          </div>

          {/* Right side: health indicator + invitations bell + user menu */}
          <div className="flex items-center gap-3">
            {isAuthenticated && <HealthIndicator />}
            {isAuthenticated && <InvitationsBell />}
            <UserMenu />
          </div>
        </div>

        {/* Dropdown Navigation - only when authenticated and menu open */}
        {isAuthenticated && menuOpen && (
          <div className="border-t border-gray-200 py-4" ref={dropdownRef}>
            <nav className="flex flex-col gap-2">
              {visibleLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={() => setMenuOpen(false)}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    pathname === link.href
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
